import { LabelOption } from '@/api/api';
import {
  GridFilterOperator,
  getGridStringOperators,
  getGridDateOperators,
  getGridBooleanOperators,
} from '@mui/x-data-grid-pro';
import { getCreatorEmailValue } from '@/utils/dataGridValueGetters';
import {
  RenderCreatedAt,
  RenderCreator,
  RenderDescription,
  RenderDetails,
  RenderLabel,
  RenderName,
} from './cellDefinitions';
import { LabelFilterInput } from './LabelFilterInput';
import { FolderContent } from '@/api/generatedApi';
import { StatusFilterInput } from './StatusFilterInput';
import { CopiableGridColDef } from '@/types/copiableColumn';
import { getAssetName } from '@/utils/assets';
import { dateFormat } from '@/utils/format';

const getNameFilterOperators = () =>
  getGridStringOperators().filter(op => ['contains', 'startsWith', 'endsWith', 'isEmpty'].includes(op.value));

const getDateFilterOperators = () =>
  getGridDateOperators().filter(op =>
    ['is', 'after', 'before', 'onOrAfter', 'onOrBefore', 'isEmpty'].includes(op.value)
  );

const getCreatorFilterOperators = () =>
  getGridStringOperators().filter(op => ['contains', 'equals', 'isEmpty'].includes(op.value));

const getStatusFilterOperators = (): GridFilterOperator[] =>
  getGridStringOperators()
    .filter(op => op.value === 'isAnyOf')
    .map(op => ({
      ...op,
      // eslint-disable-next-line unicorn/no-null
      getApplyFilterFn: () => null,
      InputComponent: StatusFilterInput,
    }));

const getExtensionFilterOperators = (): GridFilterOperator[] =>
  getGridStringOperators()
    .filter(op => ['equals'].includes(op.value))
    .map(op => ({
      ...op,
      label: 'is',
      // eslint-disable-next-line unicorn/no-null
      getApplyFilterFn: () => null,
    }));

const getExtensionFromPath = (row: FolderContent): string => {
  if (row.contentType !== 'asset') return '';
  const path = row.path ?? '';
  const lastDot = path.lastIndexOf('.');
  return lastDot === -1 ? '' : path.slice(lastDot + 1).toLowerCase();
};

const getLabelFilterOperators = (labelOptions?: LabelOption[]): GridFilterOperator[] => {
  if (labelOptions && labelOptions.length > 0) {
    return getGridStringOperators()
      .filter(op => op.value === 'isAnyOf')
      .map(
        op =>
          ({
            ...op,
            // eslint-disable-next-line unicorn/no-null
            getApplyFilterFn: () => null,
            InputComponent: LabelFilterInput,
            InputComponentProps: { labelOptions },
          }) as GridFilterOperator
      );
  }
  return getGridStringOperators()
    .filter(op => ['contains', 'equals', 'isEmpty'].includes(op.value))
    .map(op => ({
      ...op,
      // eslint-disable-next-line unicorn/no-null
      getApplyFilterFn: () => null,
    }));
};

const getLabelColumn = (labelOptions?: LabelOption[]): CopiableGridColDef<FolderContent> => ({
  field: 'label',
  headerName: 'Label',
  width: 230,
  filterable: true,
  filterOperators: getLabelFilterOperators(labelOptions),
  sortable: true,
  renderCell: RenderLabel,
  valueGetter: (_value: unknown, row: FolderContent) =>
    row.contentType === 'asset' ? row.label?.summary?.name : undefined,
  getCopyValue: row => (row.contentType === 'asset' ? row.label?.summary?.name : undefined),
});

export const getBaseColumns = (options?: { labelOptions?: LabelOption[] }): CopiableGridColDef<FolderContent>[] => {
  const columns: CopiableGridColDef<FolderContent>[] = [
    {
      field: 'name',
      headerName: 'Name',
      width: 450,
      minWidth: 450,
      filterable: true,
      filterOperators: getNameFilterOperators(),
      sortable: true,
      renderCell: RenderName,
      getCopyValue: row => (row.contentType === 'asset' ? getAssetName(row) : row.name),
    },
    {
      field: 'details',
      headerName: 'Details',
      minWidth: 170,
      filterable: false,
      sortable: false,
      renderCell: RenderDetails,
    },
    {
      field: 'description',
      headerName: 'Description',
      minWidth: 200,
      flex: 1,
      filterable: false,
      sortable: false,
      renderCell: RenderDescription,
    },
    {
      field: 'state',
      headerName: 'Status',
      width: 100,
      filterable: true,
      filterOperators: getStatusFilterOperators(),
      sortable: false,
      valueGetter: (_value: unknown, row: FolderContent) => (row.contentType === 'asset' ? row.state : undefined),
      getCopyValue: row => (row.contentType === 'asset' ? row.state : undefined),
    },
    {
      field: 'extension',
      headerName: 'Extension',
      width: 100,
      filterable: true,
      filterOperators: getExtensionFilterOperators(),
      sortable: false,
      valueGetter: (_value: unknown, row: FolderContent) => getExtensionFromPath(row),
      getCopyValue: row => getExtensionFromPath(row) || undefined,
    },
    {
      field: 'createdAt',
      headerName: 'Created',
      width: 100,
      filterable: true,
      filterOperators: getDateFilterOperators(),
      sortable: true,
      renderCell: RenderCreatedAt,
      type: 'date',
      valueGetter: (value: string | Date | undefined) => (value ? new Date(value) : undefined),
      getCopyValue: row => (row.createdAt ? dateFormat(row.createdAt) : undefined),
    },
    {
      field: 'creator',
      headerName: 'Created By',
      filterable: true,
      filterOperators: getCreatorFilterOperators(),
      sortable: true,
      renderCell: RenderCreator,
      valueGetter: getCreatorEmailValue,
      minWidth: 130,
      flex: 2,
      getCopyValue: row => row.creator?.email,
    },
    {
      field: 'isAssembly',
      headerName: 'Assembly',
      type: 'boolean',
      width: 100,
      filterable: true,
      filterOperators: getGridBooleanOperators(),
      sortable: false,
      valueGetter: (_value: unknown, row: FolderContent) =>
        row.contentType === 'asset' ? (row.isAssembly ?? false) : undefined,
    },
    {
      field: 'isRootAssembly',
      headerName: 'Root Assembly',
      type: 'boolean',
      width: 120,
      filterable: true,
      filterOperators: getGridBooleanOperators(),
      sortable: false,
      valueGetter: (_value: unknown, row: FolderContent) =>
        row.contentType === 'asset' ? (row.isRootAssembly ?? false) : undefined,
    },
  ];

  columns.splice(2, 0, getLabelColumn(options?.labelOptions));

  return columns;
};
